import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import './Profiles.css';

const PlateManagement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [ranges, setRanges] = useState([]);
  const [transitos, setTransitos] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [showRangeModal, setShowRangeModal] = useState(false);
  const [rangeForm, setRangeForm] = useState({
    transito_id: '',
    prefix: '',
    start_number: '',
    end_number: '',
    vehicle_type: 'carro',
    suffix: ''
  });

  // Pre-cargar el transito_id si es admin de un tránsito
  useEffect(() => {
    if (showRangeModal && user?.role === 'admin' && user?.transito_id) {
      setRangeForm(prev => ({ ...prev, transito_id: user.transito_id }));
    }
  }, [showRangeModal, user]);

  useEffect(() => {
    if (user?.role !== 'superadmin' && user?.role !== 'admin') {
      navigate('/dashboard');
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rangesRes, transitosRes, statsRes] = await Promise.all([
        axios.get('/api/transitos/ranges'),
        axios.get('/api/transitos'),
        axios.get('/api/transitos/stats/plates')
      ]);
      setRanges(rangesRes.data);
      setTransitos(transitosRes.data);
      setStats(statsRes.data);
      setError('');
    } catch (err) {
      setError('Error al cargar datos de placas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRange = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/transitos/ranges', rangeForm);
      fetchData();
      setShowRangeModal(false);
      setRangeForm({ transito_id: '', prefix: '', start_number: '', end_number: '', vehicle_type: 'carro', suffix: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear rango');
    }
  };

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = ranges.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="🆔 Gestión de Placas" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          {/* Estadísticas Rápidas */}
          <div className="stats-grid">
            {stats.map((s, idx) => (
              <div key={idx} className="stat-card info">
                <div className="stat-icon">
                  {s.vehicle_type === 'moto' ? '🏍️' : s.vehicle_type === 'moto_carro' ? '🛺' : '🚗'}
                </div>
                <div className="stat-details">
                  <span className="stat-label">{s.transito_name} ({s.vehicle_type || 'General'})</span>
                  <span className="stat-value">{s.disponibles} / {s.total}</span>
                  <small style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Disponibles / Total</small>
                </div>
              </div>
            ))}
          </div>

          <div className="page-header">
            <div className="header-title">
              <h2>Rangos de Placas Asignados</h2>
              <p className="subtitle">Configura los prefijos y números disponibles para cada tránsito</p>
            </div>
            {(user?.role === 'superadmin' || (user?.role === 'admin' && user?.transito_id)) && (
              <button onClick={() => setShowRangeModal(true)} className="btn-primary">
                ➕ Asignar Rango
              </button>
            )}
          </div>

          {loading ? (
            <p className="loading">Cargando rangos...</p>
          ) : (
            <div className="table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Tránsito</th>
                    <th>Tipo</th>
                    <th>Prefijo</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Actual</th>
                    <th>Disponibles</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(r => (
                    <tr key={r.id}>
                      <td className="user-name">{r.transito_name}</td>
                      <td>
                        <span className={`vehicle-type-badge ${r.vehicle_type}`}>
                          {r.vehicle_type === 'moto' ? '🏍️ Moto' : 
                           r.vehicle_type === 'moto_carro' ? '🛺 Moto Carro' : 
                           '🚗 Carro'}
                        </span>
                      </td>
                      <td>
                        <strong>{r.prefix}</strong>
                        {r.suffix && <span style={{ color: '#64748b', marginLeft: '4px' }}>- {r.suffix}</span>}
                      </td>
                      <td>{r.start_number}</td>
                      <td>{r.end_number}</td>
                      <td>{r.current_number}</td>
                      <td>
                        <span className="plate-badge-sm" style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', fontWeight: '800' }}>
                          {r.disponibles}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${r.is_active ? 'activo' : 'inactivo'}`}>
                          {r.is_active ? 'Activo' : 'Finalizado'}
                        </span>
                      </td>
                      <td className="actions">
                        <button 
                          onClick={() => navigate(`/plates/inventory/${r.transito_id}?range_id=${r.id}`)} 
                          className="btn-action btn-view"
                          title="Ver Inventario Detallado"
                        >
                          👁️ Detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination 
                currentPage={currentPage}
                totalItems={ranges.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>

      {showRangeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Asignar Nuevo Rango de Placas</h3>
            <form onSubmit={handleSaveRange}>
              {user?.role === 'superadmin' ? (
                <div className="form-group">
                  <label>Seleccionar Tránsito</label>
                  <select 
                    value={rangeForm.transito_id} 
                    onChange={(e) => setRangeForm({...rangeForm, transito_id: e.target.value})}
                    required
                  >
                    <option value="">Seleccione un tránsito...</option>
                    {transitos.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Tránsito</label>
                  <input 
                    type="text" 
                    value={transitos.find(t => t.id === parseInt(user?.transito_id))?.name || 'Cargando...'} 
                    disabled 
                    style={{ background: '#f1f5f9', fontWeight: 'bold' }}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Tipo de Vehículo</label>
                <select 
                  value={rangeForm.vehicle_type} 
                  onChange={(e) => setRangeForm({...rangeForm, vehicle_type: e.target.value})}
                  required
                >
                  <option value="carro">🚗 Carro (Letras + Números)</option>
                  <option value="moto">🏍️ Moto (Letras + Números + Letra)</option>
                  <option value="moto_carro">🛺 Moto Carro (Números + Letras)</option>
                  <option value="otro">📦 Otro</option>
                </select>
              </div>

              <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
                <small style={{ color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '5px' }}>
                  Estructura detectada:
                </small>
                {rangeForm.vehicle_type === 'moto' ? (
                  <span style={{ fontSize: '13px', color: '#2563eb' }}>
                    <strong>3 Letras</strong> (Prefijo) + <strong>2 Números</strong> + <strong>1 Letra</strong> (Sufijo)
                  </span>
                ) : rangeForm.vehicle_type === 'moto_carro' ? (
                  <span style={{ fontSize: '13px', color: '#2563eb' }}>
                    <strong>3 Números</strong> + <strong>3 Letras</strong> (Prefijo)
                  </span>
                ) : (
                  <span style={{ fontSize: '13px', color: '#2563eb' }}>
                    <strong>3 Letras</strong> (Prefijo) + <strong>3 Números</strong>
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>{rangeForm.vehicle_type === 'moto_carro' ? 'Letras Finales' : 'Prefijo de Letras'}</label>
                <input
                  type="text"
                  maxLength="3"
                  placeholder="Ej: ABC"
                  value={rangeForm.prefix}
                  onChange={(e) => setRangeForm({...rangeForm, prefix: e.target.value.toUpperCase()})}
                  required
                />
              </div>

              {rangeForm.vehicle_type === 'moto' && (
                <div className="form-group">
                  <label>Letra Final (Sufijo)</label>
                  <input
                    type="text"
                    maxLength="1"
                    placeholder="Ej: A"
                    value={rangeForm.suffix}
                    onChange={(e) => setRangeForm({...rangeForm, suffix: e.target.value.toUpperCase()})}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label>Número Inicial</label>
                <input
                  type="number"
                  min="0"
                  max={rangeForm.vehicle_type === 'moto' ? "99" : "999"}
                  placeholder={rangeForm.vehicle_type === 'moto' ? "00" : "000"}
                  value={rangeForm.start_number}
                  onChange={(e) => setRangeForm({...rangeForm, start_number: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Número Final</label>
                <input
                  type="number"
                  min="0"
                  max={rangeForm.vehicle_type === 'moto' ? "99" : "999"}
                  placeholder={rangeForm.vehicle_type === 'moto' ? "99" : "999"}
                  value={rangeForm.end_number}
                  onChange={(e) => setRangeForm({...rangeForm, end_number: e.target.value})}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowRangeModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Generar Placas
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlateManagement;
